import React, { useEffect, useMemo, useState } from "react";
import api, { fileViewUrl, formatErr } from "../lib/api";
import { X, Search, Check, Users, UserPlus } from "lucide-react";
import { toast } from "sonner";

/**
 * Allot a household to one or more students. Faculty/Manager only.
 *
 * Props:
 *   - household: { id, head_of_family, village, assigned_to?, assignees? }
 *   - onClose()
 *   - onSaved(updatedHousehold) — called with the fresh household payload (incl. assignees)
 */
export default function AllotModal({ household, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set(household?.assigned_to || []));
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/students")
      .then((r) => setStudents(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      (s.name || "").toLowerCase().includes(q)
      || (s.email || "").toLowerCase().includes(q)
      || (s.roll_number || "").toLowerCase().includes(q)
      || (s.registration_number || "").toLowerCase().includes(q)
      || (s.batch || "").toLowerCase().includes(q)
      || (s.degree || "").toLowerCase().includes(q),
    );
  }, [students, query]);

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/households/${household.id}/assignments`, {
        student_ids: Array.from(selected),
      });
      toast.success(`Allotted to ${selected.size} student${selected.size === 1 ? "" : "s"}`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-2xl p-6 flex flex-col max-h-[85vh]" data-testid="allot-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold inline-flex items-center gap-1.5">
              <UserPlus className="h-3 w-3" /> Allot family to students
            </div>
            <h2 className="font-heading text-2xl font-semibold mt-1">{household.head_of_family}</h2>
            <p className="text-xs text-brand-muted mt-0.5">{household.village} · Select one or more students to provide ongoing care</p>
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, roll, registration, batch, course…"
            className="w-full h-11 pl-9 pr-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none text-sm"
            data-testid="allot-search"
          />
        </div>

        <div className="mt-2 text-[11px] uppercase tracking-wider font-bold text-brand-muted flex items-center justify-between">
          <span>{filtered.length} student{filtered.length === 1 ? "" : "s"}</span>
          <span className="inline-flex items-center gap-1 text-brand-primary">
            <Users className="h-3 w-3" /> {selected.size} selected
          </span>
        </div>

        <div className="mt-2 flex-1 overflow-y-auto scrollbar-thin -mx-1 px-1">
          {loading ? (
            <div className="text-sm text-brand-muted p-4">Loading students…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-brand-muted p-6 text-center">No students match your search.</div>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((s) => {
                const checked = selected.has(s.id);
                const initials = (s.name || "?").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggle(s.id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-md border transition-colors text-left ${checked ? "border-brand-primary bg-brand-primary/5" : "border-brand-border bg-white hover:bg-brand-surfaceAlt"}`}
                    data-testid={`allot-student-${s.id}`}
                  >
                    {s.photo_path ? (
                      <img src={fileViewUrl(s.photo_path)} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
                    ) : (
                      <span className="h-10 w-10 rounded-full grid place-items-center font-heading font-semibold text-sm bg-brand-accent/15 text-[#9C5240] shrink-0">{initials || "?"}</span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{s.name}</div>
                      <div className="text-[11px] text-brand-muted mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        {s.degree && <span>{s.degree}</span>}
                        {s.batch && <span>Batch {s.batch}</span>}
                        {s.roll_number && <span>Roll {s.roll_number}</span>}
                        {s.registration_number && <span>Reg {s.registration_number}</span>}
                      </div>
                    </div>
                    <span className={`h-5 w-5 rounded grid place-items-center shrink-0 border-2 ${checked ? "bg-brand-primary border-brand-primary text-white" : "border-brand-border"}`}>
                      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-brand-border flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-bold shadow-sm disabled:opacity-60"
            data-testid="allot-save-btn"
          >
            {saving ? "Saving…" : `Save (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}
